import { Check, X } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../../components/ui/common/tooltip";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { formValuesToRoute, routeToFormValues } from "../utils/chemicalRecords.utils";
import { RouteFormValues, SelectOptionList, TransportRoute } from "../types/chemicalRecords.types";

interface EditRouteFormProps {
  route: TransportRoute;
  chemId: string;
  transportOptions: SelectOptionList;
  onUpdateRoute: (chemId: string, routeId: string, patch: Partial<TransportRoute>) => void;
  onDone: () => void;
}

/** Boxed edit form for an existing transport route. */
const EditRouteForm: React.FC<EditRouteFormProps> = ({
  route,
  chemId,
  transportOptions,
  onUpdateRoute,
  onDone
}) => {
  const form = useForm<RouteFormValues>({ defaultValues: routeToFormValues(route) });

  const handleSave = form.handleSubmit(data => {
    onUpdateRoute(chemId, route.id, formValuesToRoute(data));
    onDone();
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSave}
        className="tw-border tw-border-primary/30 tw-rounded tw-p-2 tw-bg-primary/5 tw-space-y-2"
      >
        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          <FormSelect
            control={form.control}
            name="type"
            label="Transport Type"
            required
            options={transportOptions}
            placeholder="Select type"
            errorMessage="Type is required"
          />
          <FormInputNumber
            control={form.control}
            name="distance"
            label="Distance"
            placeholder="0"
            rightContentEnable
            rightContent="km"
          />
        </div>
        <div className="tw-flex tw-justify-end tw-gap-1.5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="submit" variant="default" size="icon" className="tw-h-7 tw-w-7">
                  <Check className="tw-h-3 tw-w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="tw-text-xs">Save changes</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="tw-h-7 tw-w-7"
                  onClick={onDone}
                >
                  <X className="tw-h-3 tw-w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="tw-text-xs">Cancel</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </form>
    </Form>
  );
};

export default EditRouteForm;
